import type { ClientMessage, ErrorCode, PublicGuest, ServerMessage } from '../../shared/protocol';
import { isRoomId } from '../../shared/protocol';
import { storage } from './storage';
import { recordVisit } from './telemetry';
import { savedLookCode } from './look';
import { LOOK_CHANGE_MS } from '../../shared/appearance';

export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'offline' | 'error' | 'full' | 'busy';
export type RoomState = { status: ConnectionStatus; guests: PublicGuest[]; you: string | null; roomId: string | null; offset: number };
export const EMPTY_ROOM: RoomState = { status: 'idle', guests: [], you: null, roomId: null, offset: 0 };

export class RoomClient {
  private socket: WebSocket | null = null;
  private retry: ReturnType<typeof setTimeout> | undefined;
  private handshake: ReturnType<typeof setTimeout> | undefined;
  private stopped = true;
  private retries = 0;
  private rematches = 0;
  private generation = 0;
  private state: RoomState = { ...EMPTY_ROOM };
  private invited = false;
  private disposed = false;
  private lookTimer: ReturnType<typeof setTimeout> | undefined;
  private pendingLook: number | null = null;
  private lookInFlight: number | null = null;
  private lookReadyAt = 0;

  constructor(private onState: (state: RoomState) => void, private onError: (error: ErrorCode) => void) {
    window.addEventListener('offline', this.offline);
    window.addEventListener('online', this.online);
  }
  private emit(patch: Partial<RoomState>) { this.state = { ...this.state, ...patch }; if (!this.disposed) this.onState(this.state); }
  private offline = () => {
    if (this.stopped) return;
    clearTimeout(this.retry); clearTimeout(this.handshake);
    this.emit({ status: 'offline' });
    this.socket?.close(4001, 'network_offline');
  };
  private online = () => { if (!this.stopped) { clearTimeout(this.retry); this.openSocket(); } };

  connect(roomId?: string) {
    this.rematches = 0;
    return this.match(roomId);
  }
  private async match(roomId?: string) {
    this.disconnect(false);
    const generation = ++this.generation;
    this.stopped = false; this.retries = 0; this.invited = !!roomId;
    this.emit({ ...EMPTY_ROOM, status: navigator.onLine ? 'connecting' : 'offline', roomId: roomId ?? null });
    try {
      const response = await fetch('/api/rooms', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(roomId ? { roomId } : {}) });
      if (response.status === 503) {
        if (generation === this.generation && !this.stopped) { this.stopped = true; this.emit({ status: 'busy' }); }
        return;
      }
      if (!response.ok) throw new Error('room_unavailable');
      const data: unknown = await response.json();
      if (!data || typeof data !== 'object' || !isRoomId((data as { roomId?: unknown }).roomId)) throw new Error('invalid_room');
      if (generation !== this.generation || this.stopped) return;
      this.emit({ roomId: (data as { roomId: string }).roomId });
      if (navigator.onLine) this.openSocket();
    } catch {
      if (generation === this.generation && !this.stopped) { this.stopped = true; this.emit({ status: 'error' }); }
    }
  }
  private openSocket() {
    if (this.stopped || !this.state.roomId || !navigator.onLine) return;
    if (this.socket && (this.socket.readyState === WebSocket.CONNECTING || this.socket.readyState === WebSocket.OPEN)) return;
    this.clearLookQueue(); // hello carries the latest saved choice after any reconnect.
    const roomId = this.state.roomId;
    this.emit({ status: this.state.you ? 'reconnecting' : 'connecting' });
    const url = new URL(`/api/rooms/${roomId}/connect`, window.location.href); url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(url); this.socket = socket;
    this.handshake = setTimeout(() => { if (this.socket === socket && this.state.status !== 'connected') socket.close(4000, 'handshake_timeout'); }, 10000);
    socket.addEventListener('open', () => {
      if (this.socket !== socket || this.stopped) { socket.close(1000); return; }
      const resumeToken = storage.get(`session.${roomId}`);
      const appearance = savedLookCode();
      socket.send(JSON.stringify({ type: 'hello', ...(resumeToken ? { resumeToken } : {}), ...(appearance !== null ? { appearance } : {}) } satisfies ClientMessage));
    });
    socket.addEventListener('message', event => {
      if (this.socket !== socket || this.stopped || typeof event.data !== 'string') return;
      let message: ServerMessage;
      try { message = JSON.parse(event.data) as ServerMessage; } catch { return; }
      if (message.type === 'welcome') {
        clearTimeout(this.handshake); this.retries = 0;
        storage.set(`session.${roomId}`, message.resumeToken);
        this.emit({ status: 'connected', guests: message.guests, you: message.you, roomId: message.roomId, offset: message.serverTime - Date.now() });
        const saved = savedLookCode();
        if (saved !== null && message.guests.find(guest => guest.id === message.you)?.appearance !== saved) this.send({ type: 'look', appearance: saved });
        recordVisit();
      } else if (message.type === 'state') {
        this.emit({ guests: message.guests, offset: message.serverTime - Date.now() });
        if (this.lookInFlight !== null && message.guests.find(guest => guest.id === this.state.you)?.appearance === this.lookInFlight) {
          this.lookInFlight = null;
          // Start the pause after the server acknowledges, so network latency cannot shorten it.
          this.lookReadyAt = Date.now() + LOOK_CHANGE_MS;
          this.flushLook();
        }
      } else if (message.type === 'error') {
        if (message.code === 'full') {
          this.stopped = true; clearTimeout(this.retry); clearTimeout(this.handshake);
          if (!this.invited && this.rematches < 3) { this.rematches++; void this.match(); }
          else this.emit({ status: 'full' });
        } else this.onError(message.code);
      }
    });
    socket.addEventListener('close', event => {
      if (this.socket !== socket) return;
      this.socket = null; clearTimeout(this.handshake);
      if (this.stopped) return;
      if (event.code === 4002) { this.stopped = true; this.emit({ status: 'idle', guests: [], you: null }); return; }
      if (!navigator.onLine) { this.emit({ status: 'offline' }); return; }
      if (++this.retries > 8) { this.stopped = true; this.emit({ status: 'error' }); return; }
      this.emit({ status: 'reconnecting' });
      this.retry = setTimeout(() => this.openSocket(), Math.min(4000, 700 * 1.5 ** this.retries));
    });
    socket.addEventListener('error', () => { /* browser close event schedules reconnection */ });
  }
  send(message: ClientMessage): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN || this.state.status !== 'connected') return false;
    if (message.type === 'look') { this.pendingLook = message.appearance; this.flushLook(); return true; }
    this.socket.send(JSON.stringify(message)); return true;
  }
  private flushLook() {
    clearTimeout(this.lookTimer);
    if (this.pendingLook === null || this.lookInFlight !== null || this.socket?.readyState !== WebSocket.OPEN || this.state.status !== 'connected') return;
    const delay = this.lookReadyAt - Date.now();
    if (delay > 0) { this.lookTimer = setTimeout(() => this.flushLook(), delay); return; }
    this.lookInFlight = this.pendingLook; this.pendingLook = null;
    this.socket.send(JSON.stringify({ type: 'look', appearance: this.lookInFlight } satisfies ClientMessage));
  }
  private clearLookQueue() {
    clearTimeout(this.lookTimer); this.pendingLook = null; this.lookInFlight = null; this.lookReadyAt = 0;
  }
  disconnect(update = true) {
    this.clearLookQueue();
    this.stopped = true; this.generation++; clearTimeout(this.retry); clearTimeout(this.handshake);
    const socket = this.socket; this.socket = null;
    if (socket && socket.readyState < WebSocket.CLOSING) socket.close(1000, 'guest_leaving');
    if (update) this.emit({ ...EMPTY_ROOM });
  }
  dispose() { this.disposed = true; this.disconnect(false); window.removeEventListener('online', this.online); window.removeEventListener('offline', this.offline); }
}
